import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { LibraryRoleCreate, LibraryRolePatch, EventRolesSetBody } from '../types'

// Global Roles catalogue + per-event EventRoles toggle (mig 039,
// Roster rebuild PR R1).
//
// Mirrors useLibraryComponents / useLibraryActivities: long stale time on
// the catalogue (changes infrequently, every per-event Configure picker
// needs it), short stale time on the per-event toggle (changes per
// admin action). Manual invalidation on every mutation keeps things
// fresh.

// ---- Global Roles catalogue ----------------------------------------------

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: ApiClient.roles,
    staleTime: 30 * 60_000
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LibraryRoleCreate) => ApiClient.createRole(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] })
  })
}

export function usePatchRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: LibraryRolePatch }) =>
      ApiClient.patchRole(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] })
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteRole(id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] })
  })
}

// Replace the default "roles to call for" set (item 1). Used by the volunteer
// call composer's "Set as default set" action.
export function useSetRoleDefaults() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (roleIds: number[]) => ApiClient.setRoleDefaults(roleIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] })
  })
}

// ---- Per-event role toggle -----------------------------------------------

export function useEventRoles(eventId: number) {
  return useQuery({
    queryKey: ['event-roles', eventId],
    queryFn: () => ApiClient.eventRoles(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useSetEventRoles(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventRolesSetBody) => ApiClient.setEventRoles(eventId, body),
    onSuccess: () => {
      // Per-event configure picker + the grid both depend on this set.
      qc.invalidateQueries({ queryKey: ['event-roles', eventId] })
      qc.invalidateQueries({ queryKey: ['roster', eventId] })
    }
  })
}
